import React, { useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  message
} from 'antd'
import { DollarOutlined, FileTextOutlined, LogoutOutlined, ToolOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import type { RentAddition, Vehicle, VehicleAdjustment } from '../types'
import { matchVehicle } from './vehicleManagerUtils'

const { Text, Title } = Typography
const { Option } = Select

type AdjustmentFormValues = {
  vehicleId?: string
  amount?: number
  note?: string
  effectiveDate?: Dayjs
}

type PartsAdjustmentType = 'parts' | 'repair' | 'vehicle_value' | 'rent_addition'

const paidStatuses = new Set(['cashier_done', 'accounted', 'completed'])

const getEntityId = (record: any) => String(record?._id || record?.id || '').trim()
const getVehicleKey = (vehicle: Vehicle) => String(vehicle._id || vehicle.id || '').trim()
const getPlateKey = (value: any) => String(value || '').trim()

const dedupeById = <T extends any>(items: T[]) => {
  const seen = new Set<string>()
  return items.filter(item => {
    const key = getEntityId(item)
    if (!key) return true
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const matchesOwnedVehicleRecord = (record: any, vehicle: Vehicle) => {
  const vehicleKey = getVehicleKey(vehicle)
  const recordVehicleKey = String(record?.vehicleId || '').trim()
  if (vehicleKey && recordVehicleKey) {
    return recordVehicleKey === vehicleKey
  }
  if (vehicleKey || recordVehicleKey) {
    return false
  }
  return getPlateKey(record?.plate) === getPlateKey(vehicle.plate)
}

const getAdjustmentLabel = (type: PartsAdjustmentType) => {
  switch (type) {
    case 'vehicle_value':
      return '车辆原值'
    case 'repair':
      return '工时费用'
    case 'rent_addition':
      return '补录租金'
    default:
      return '配件'
  }
}

const PartsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles, fetchVehicles } = useVehicleStore()

  const [loading, setLoading] = useState(true)
  const [receipts, setReceipts] = useState<any[]>([])
  const [adjustments, setAdjustments] = useState<VehicleAdjustment[]>([])
  const [rentAdditions, setRentAdditions] = useState<RentAddition[]>([])
  const [detailVehicle, setDetailVehicle] = useState<Vehicle | null>(null)
  const [detailVisible, setDetailVisible] = useState(false)
  const [adjustVisible, setAdjustVisible] = useState(false)
  const [adjustVehicle, setAdjustVehicle] = useState<Vehicle | null>(null)
  const [editingRecord, setEditingRecord] = useState<VehicleAdjustment | RentAddition | null>(null)
  const [editingType, setEditingType] = useState<PartsAdjustmentType>('parts')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form] = Form.useForm<AdjustmentFormValues>()

  useEffect(() => {
    void loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [vehicleResult, receiptRes, adjustmentRes, rentAddRes] = await Promise.allSettled([
        fetchVehicles(),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getVehicleRentAdditions()
      ])

      if (vehicleResult.status === 'rejected') {
        console.error(vehicleResult.reason)
      }

      const receiptData =
        receiptRes.status === 'fulfilled' && receiptRes.value.success && receiptRes.value.data
          ? receiptRes.value.data
          : []
      const adjustmentsRaw =
        adjustmentRes.status === 'fulfilled' && adjustmentRes.value.success && adjustmentRes.value.data
          ? adjustmentRes.value.data
          : []
      const rentAddRaw =
        rentAddRes.status === 'fulfilled' && rentAddRes.value.success && rentAddRes.value.data
          ? rentAddRes.value.data
          : []

      setReceipts(dedupeById(receiptData))

      const uniqueAdjustments = dedupeById(adjustmentsRaw)
      const uniqueRentAdds = dedupeById(rentAddRaw)
      const rentAddsFromAdjustments = uniqueAdjustments.filter((adj: any) => adj.type === 'rent_addition')

      setAdjustments(uniqueAdjustments.filter((adj: any) => adj.type !== 'rent_addition'))
      setRentAdditions(dedupeById([...uniqueRentAdds, ...rentAddsFromAdjustments]))
      return true
    } catch (error) {
      console.error(error)
      message.error('加载失败')
      return false
    } finally {
      setLoading(false)
    }
  }

  const getRecordId = (record: any) => record?._id || record?.id

  const upsertAdjustmentState = (type: PartsAdjustmentType, record: VehicleAdjustment | RentAddition) => {
    const recordId = getRecordId(record)
    if (!recordId) return

    if (type === 'rent_addition') {
      setRentAdditions(prev => {
        const exists = prev.some(item => getRecordId(item) === recordId)
        return exists
          ? prev.map(item => (getRecordId(item) === recordId ? { ...item, ...record } : item))
          : [record as RentAddition, ...prev]
      })
      return
    }

    setAdjustments(prev => {
      const exists = prev.some(item => getRecordId(item) === recordId)
      return exists
        ? prev.map(item => (getRecordId(item) === recordId ? { ...item, ...record } : item))
        : [record as VehicleAdjustment, ...prev]
    })
  }

  const getReceiptItems = (receipt: any) => {
    const raw = receipt?.items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
        return []
      }
    }
    return []
  }

  const getVehicleReceiptItems = (vehicle: Vehicle) => {
    const results: any[] = []
    receipts.forEach(receipt => {
      if (!paidStatuses.has(receipt.status)) return
      const items = getReceiptItems(receipt)
      items.forEach((item: any) => {
        if (matchVehicle(item, vehicle)) {
          results.push({ ...item, _receipt: receipt })
        }
      })
    })
    return results
  }

  const getItemBasisAmount = (item: any) =>
    Number(
      item?.actualAmountDraft ??
      item?.receivedAmount ??
      item?.verifiedAmount ??
      item?.expectedAmount ??
      item?.amount ??
      0
    ) || 0

  const getReceiptRentActualTotal = (receipt: any) => {
    const raw = receipt || {}
    const items = getReceiptItems(raw)
    const depositFromItems = items.reduce((sum: number, current: any) => sum + (Number(current?.depositAmount || 0) || 0), 0)
    const depositTotal = Number(raw?.depositAmount ?? depositFromItems ?? 0) || 0
    const actualTotal = Number(raw?.actualAmount ?? raw?.actualAmountDraft ?? 0) || 0
    return Math.max(actualTotal - depositTotal, 0)
  }

  const getReceiptAmount = (item: any, receipt?: any) => {
    if (item?.actualAmount != null && !Number.isNaN(Number(item.actualAmount))) {
      return Number(item.actualAmount || 0)
    }

    const raw = receipt || item._receipt
    const received = Number(item.receivedAmount ?? item.verifiedAmount ?? item.expectedAmount ?? item.amount ?? 0) || 0
    if (!raw) return received

    const items = getReceiptItems(raw)
    const actualTotal = getReceiptRentActualTotal(raw)
    if (actualTotal > 0) {
      const explicitActualTotal = items.reduce((sum: number, current: any) => {
        if (current?.actualAmount == null || Number.isNaN(Number(current.actualAmount))) return sum
        return sum + (Number(current.actualAmount || 0) || 0)
      }, 0)

      const pendingItems = items.filter((current: any) =>
        current?.actualAmount == null || Number.isNaN(Number(current.actualAmount))
      )
      const pendingActualTotal = Math.max(actualTotal - explicitActualTotal, 0)

      if (pendingItems.length === 1) {
        return pendingActualTotal
      }

      if (pendingItems.length > 1) {
        const totalBasis = pendingItems.reduce((sum: number, current: any) => sum + getItemBasisAmount(current), 0)
        const basis = getItemBasisAmount(item)
        if (totalBasis > 0 && basis > 0) {
          return (pendingActualTotal * basis) / totalBasis
        }
        return pendingActualTotal / pendingItems.length
      }
    }

    return received
  }

  const getVehicleAdjustments = (vehicle: Vehicle) =>
    dedupeById(adjustments.filter(adj => matchesOwnedVehicleRecord(adj, vehicle)))

  const getVehicleRentAdditions = (vehicle: Vehicle) =>
    dedupeById(rentAdditions.filter(add => matchesOwnedVehicleRecord(add, vehicle)))

  const vehicleSummaries = useMemo(() => {
    return vehicles.map(vehicle => {
      const receiptItems = getVehicleReceiptItems(vehicle)
      const paidTotal = receiptItems.reduce((sum, item) => sum + getReceiptAmount(item, item._receipt), 0)
      const vehicleAdjustments = getVehicleAdjustments(vehicle)
      const vehicleValueTotal = vehicleAdjustments
        .filter(adj => adj.type === 'vehicle_value')
        .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
      const partsTotal = vehicleAdjustments
        .filter(adj => adj.type === 'parts')
        .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
      const repairTotal = vehicleAdjustments
        .filter(adj => adj.type === 'repair')
        .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
      const rentAddTotal = getVehicleRentAdditions(vehicle)
        .reduce((sum, add) => sum + (Number(add.amount) || 0), 0)
      const rentTotal = paidTotal + rentAddTotal
      const costTotal = vehicleValueTotal + partsTotal + repairTotal
      const revenue = rentTotal - costTotal

      return {
        vehicle,
        vehicleValueTotal,
        partsTotal,
        repairTotal,
        rentAddTotal,
        rentTotal,
        costTotal,
        revenue
      }
    })
  }, [vehicles, receipts, adjustments, rentAdditions])

  const filteredSummaries = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    if (!keyword) return vehicleSummaries

    return vehicleSummaries.filter(({ vehicle }) => {
      const plate = String(vehicle.plate || '').toLowerCase()
      const company = String(vehicle.company || '').toLowerCase()
      const contact1 = String(vehicle.contactPerson || '').toLowerCase()
      const contact2 = String(vehicle.contactPerson2 || '').toLowerCase()
      const phone = String(vehicle.phone || '')
      const phone2 = String(vehicle.phone2 || '')
      return (
        plate.includes(keyword) ||
        company.includes(keyword) ||
        contact1.includes(keyword) ||
        contact2.includes(keyword) ||
        phone.includes(keyword) ||
        phone2.includes(keyword)
      )
    })
  }, [vehicleSummaries, searchKeyword])

  const totalRentIncome = useMemo(() => {
    const paidTotal = receipts
      .filter(receipt => paidStatuses.has(receipt.status))
      .reduce((sum, receipt) => {
        const items = getReceiptItems(receipt)
        return sum + items.reduce((inner: number, item: any) => inner + getReceiptAmount(item, receipt), 0)
      }, 0)

    const rentAddTotal = rentAdditions.reduce((sum, add) => sum + (Number(add.amount) || 0), 0)
    return paidTotal + rentAddTotal
  }, [receipts, rentAdditions])

  const totalVehicleValue = useMemo(
    () => adjustments.filter(adj => adj.type === 'vehicle_value').reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0),
    [adjustments]
  )

  const totalParts = useMemo(
    () => adjustments.filter(adj => adj.type === 'parts').reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0),
    [adjustments]
  )

  const totalRepair = useMemo(
    () => adjustments.filter(adj => adj.type === 'repair').reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0),
    [adjustments]
  )

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const openAdjustmentModal = (vehicle: Vehicle | null, type: PartsAdjustmentType) => {
    setEditingType(type)
    setAdjustVehicle(vehicle)
    setEditingRecord(null)
    form.resetFields()
    if (vehicle) {
      form.setFieldsValue({ vehicleId: vehicle._id || vehicle.id })
    }
    setAdjustVisible(true)
  }

  const openEditAdjustment = (
    vehicle: Vehicle,
    record: VehicleAdjustment | RentAddition,
    type: PartsAdjustmentType
  ) => {
    setEditingType(type)
    setAdjustVehicle(vehicle)
    setEditingRecord(record)
    form.resetFields()
    form.setFieldsValue({
      vehicleId: vehicle._id || vehicle.id,
      amount: Number(record.amount || 0),
      note: record.note,
      effectiveDate: record.effectiveDate ? dayjs(record.effectiveDate) : undefined
    })
    setAdjustVisible(true)
  }

  const handleDeleteAdjustment = async (record: VehicleAdjustment | RentAddition, type: PartsAdjustmentType) => {
    const recordId = getRecordId(record)
    if (!recordId) {
      message.error('缺少记录 ID')
      return
    }

    Modal.confirm({
      title: '确认删除该记录？',
      onOk: async () => {
        const result = type === 'rent_addition'
          ? await simpleDirectCloudApi.deleteVehicleRentAddition(recordId)
          : await simpleDirectCloudApi.deleteVehicleAdjustment(recordId)

        if (!result.success) {
          message.error(result.error || '删除失败')
          return
        }

        if (type === 'rent_addition') {
          setRentAdditions(prev => prev.filter(item => getRecordId(item) !== recordId))
        } else {
          setAdjustments(prev => prev.filter(item => getRecordId(item) !== recordId))
        }

        message.success('删除成功')
        void loadData()
      }
    })
  }

  const handleSaveAdjustment = async () => {
    const values = await form.validateFields()
    const vehicleId = values.vehicleId || adjustVehicle?._id || adjustVehicle?.id
    if (!vehicleId) {
      message.error('请选择车辆')
      return
    }

    const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId || v.plate === vehicleId)
    if (!vehicle) {
      message.error('车辆不存在')
      return
    }

    const amount = Number(values.amount || 0)
    if (!amount) {
      message.error('请输入有效金额')
      return
    }
    if (editingType === 'rent_addition' && amount < 0) {
      message.error('补录租金请录入正数')
      return
    }

    const effectiveDate = values.effectiveDate
      ? dayjs(values.effectiveDate).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')

    const basePayload = {
      vehicleId: vehicle._id || vehicle.id,
      plate: vehicle.plate,
      company: vehicle.company,
      amount,
      note: values.note || '',
      effectiveDate,
      createdAt: new Date().toISOString(),
      createdBy: user?.nickName || user?.nickname || user?.username || ''
    }

    const recordId = editingRecord ? getRecordId(editingRecord) : null
    const result = editingType === 'rent_addition'
      ? (recordId
          ? await simpleDirectCloudApi.updateVehicleRentAddition(recordId, { ...basePayload, type: 'rent_addition' })
          : await simpleDirectCloudApi.addVehicleRentAddition({ ...basePayload, type: 'rent_addition' }))
      : (recordId
          ? await simpleDirectCloudApi.updateVehicleAdjustment(recordId, { ...basePayload, type: editingType } as VehicleAdjustment)
          : await simpleDirectCloudApi.addVehicleAdjustment({ ...basePayload, type: editingType } as VehicleAdjustment))

    if (!result.success) {
      message.error(result.error || '保存失败')
      return
    }

    const savedRecord = {
      ...(editingRecord || {}),
      ...basePayload,
      type: editingType,
      _id: recordId || (result.data as any)?._id || (result.data as any)?.id,
      id: recordId || (result.data as any)?._id || (result.data as any)?.id
    } as VehicleAdjustment | RentAddition

    upsertAdjustmentState(editingType, savedRecord)
    setAdjustVisible(false)
    setAdjustVehicle(null)
    setEditingRecord(null)
    form.resetFields()
    message.success('保存成功')
    void loadData()
  }

  const openDetail = (vehicle: Vehicle) => {
    setDetailVehicle(vehicle)
    setDetailVisible(true)
  }

  const renderReceiptRange = (item: any) => {
    if (item.chargeRangeStart && item.chargeRangeEnd) {
      return `${item.chargeRangeStart} ~ ${item.chargeRangeEnd}`
    }
    const receipt = item._receipt || {}
    return item.periodKey || item.dueDate || receipt.dueDate || '-'
  }

  const detailReceipts = detailVehicle ? getVehicleReceiptItems(detailVehicle) : []
  const detailAdjustments = detailVehicle ? getVehicleAdjustments(detailVehicle) : []
  const detailRentAdditions = detailVehicle ? getVehicleRentAdditions(detailVehicle) : []

  const getLeaseHistory = (vehicle: Vehicle) => {
    const history = Array.isArray(vehicle.leaseHistory) ? vehicle.leaseHistory : []
    return history
      .filter((item: any) => item && (item.company || item.contactPerson || item.startDate))
      .sort((a: any, b: any) => dayjs(b.startDate || 0).valueOf() - dayjs(a.startDate || 0).valueOf())
  }

  const getCurrentLease = (vehicle: Vehicle) => {
    const history = getLeaseHistory(vehicle)
    const currentId = vehicle.currentLeaseId
    if (currentId) {
      const match = history.find((item: any) => item.leaseId === currentId)
      if (match) return match
    }
    return history[0] || null
  }

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Text>加载中...</Text>
      </div>
    )
  }

  return (
    <div style={{ padding: 24, background: 'var(--bg)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} style={{ backgroundColor: 'var(--warning)' }} icon={<ToolOutlined />} />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {user?.nickname || user?.nickName || user?.username || 'parts'}
                </Title>
                <Text type="secondary">{user?.info || '成本端'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<FileTextOutlined />} onClick={() => openAdjustmentModal(null, 'rent_addition')}>
                补录租金
              </Button>
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>
                退出
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} md={8} xl={4}>
          <Card>
            <Statistic
              title="租金"
              value={totalRentIncome}
              valueStyle={{ color: 'var(--positive)' }}
              prefix={<DollarOutlined />}
              formatter={value => `￥${Number(value || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <Card>
            <Statistic
              title="车辆原值"
              value={totalVehicleValue}
              valueStyle={{ color: '#52c41a' }}
              formatter={value => `￥${Number(value || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <Card>
            <Statistic
              title="配件"
              value={totalParts}
              valueStyle={{ color: 'var(--warning)' }}
              formatter={value => `￥${Number(value || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <Card>
            <Statistic
              title="工时"
              value={totalRepair}
              valueStyle={{ color: '#ff7a45' }}
              formatter={value => `￥${Number(value || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <Card>
            <Statistic
              title="净收入"
              value={totalRentIncome - totalVehicleValue - totalParts - totalRepair}
              valueStyle={{ color: '#1677ff' }}
              formatter={value => `￥${Number(value || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={`车辆收款汇总 (${filteredSummaries.length})`}
        extra={(
          <Input.Search
            placeholder="车牌号/公司/联系人/电话..."
            allowClear
            onSearch={value => {
              setSearchKeyword(value)
              setCurrentPage(1)
            }}
            onChange={event => {
              if (!event.target.value) {
                setSearchKeyword('')
                setCurrentPage(1)
              }
            }}
            style={{ width: 260 }}
          />
        )}
      >
        <List
          itemLayout="vertical"
          dataSource={filteredSummaries}
          pagination={{
            current: currentPage,
            pageSize,
            total: filteredSummaries.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, size) => {
              setCurrentPage(page)
              if (size !== pageSize) {
                setPageSize(size || 10)
                setCurrentPage(1)
              }
            },
            onShowSizeChange: (_, size) => {
              setPageSize(size || 10)
              setCurrentPage(1)
            },
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`
          }}
          renderItem={({ vehicle, vehicleValueTotal, partsTotal, repairTotal, rentAddTotal, rentTotal, costTotal, revenue }) => (
            <List.Item
              key={vehicle._id || vehicle.id || vehicle.plate}
              actions={[
                <Button key="detail" type="link" onClick={() => openDetail(vehicle)}>明细</Button>,
                <Button key="rent" type="link" onClick={() => openAdjustmentModal(vehicle, 'rent_addition')}>补录租金</Button>,
                <Button key="vehicle_value" type="link" onClick={() => openAdjustmentModal(vehicle, 'vehicle_value')}>车辆原值</Button>,
                <Button key="parts" type="link" onClick={() => openAdjustmentModal(vehicle, 'parts')}>配件</Button>,
                <Button key="repair" type="link" onClick={() => openAdjustmentModal(vehicle, 'repair')}>工时费用</Button>
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                title={(
                  <Space wrap>
                    <Text strong>{vehicle.plate}</Text>
                    <Tag color="green">租金 ￥{rentTotal.toFixed(2)}</Tag>
                    <Tag color="lime">车辆原值 ￥{vehicleValueTotal.toFixed(2)}</Tag>
                    <Tag color="orange">配件 ￥{partsTotal.toFixed(2)}</Tag>
                    <Tag color="volcano">工时 ￥{repairTotal.toFixed(2)}</Tag>
                    {!!rentAddTotal && <Tag color="cyan">补录租金 ￥{rentAddTotal.toFixed(2)}</Tag>}
                    <Tag color={revenue >= 0 ? 'blue' : 'red'}>净收入 ￥{revenue.toFixed(2)}</Tag>
                  </Space>
                )}
                description={(
                  <div>
                    <Text type="secondary">公司：</Text>
                    <Text>{vehicle.company || '-'}</Text>
                    <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                    <Text>{vehicle.contactPerson || '-'}</Text>
                    <Text type="secondary" style={{ marginLeft: 12 }}>成本合计：</Text>
                    <Text>￥{costTotal.toFixed(2)}</Text>
                  </div>
                )}
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={detailVehicle ? `${detailVehicle.plate} 明细` : '车辆明细'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
      >
        {detailVehicle && (
          <div>
            <Space style={{ marginBottom: 12 }} wrap>
              <Tag color="blue">公司：{detailVehicle.company || '-'}</Tag>
              <Tag>联系人：{detailVehicle.contactPerson || '-'}</Tag>
              <Tag>合同租金：￥{Number(detailVehicle.rentAmount || 0).toFixed(2)}</Tag>
            </Space>

            <Card size="small" title="租客信息" style={{ marginBottom: 12 }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Text strong>当前租客</Text>
                {getCurrentLease(detailVehicle) ? (
                  <Space wrap>
                    <Tag color="gold">公司：{getCurrentLease(detailVehicle)?.company || '-'}</Tag>
                    <Tag>联系人：{getCurrentLease(detailVehicle)?.contactPerson || '-'}</Tag>
                    <Tag>起租：{getCurrentLease(detailVehicle)?.startDate || '-'}</Tag>
                    <Tag>到期：{getCurrentLease(detailVehicle)?.endDate || '-'}</Tag>
                  </Space>
                ) : (
                  <Text type="secondary">暂无当前租客记录</Text>
                )}

                <Text strong>历史租客</Text>
                {getLeaseHistory(detailVehicle).length ? (
                  <List
                    size="small"
                    dataSource={getLeaseHistory(detailVehicle)}
                    renderItem={(lease: any) => (
                      <List.Item>
                        <Space wrap>
                          <Text>{lease.company || '-'}</Text>
                          <Text type="secondary">联系人：{lease.contactPerson || '-'}</Text>
                          <Text type="secondary">起租：{lease.startDate || '-'}</Text>
                          <Text type="secondary">到期：{lease.endDate || '-'}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Text type="secondary">暂无历史租客</Text>
                )}
              </Space>
            </Card>

            <Card size="small" title="收款记录" style={{ marginBottom: 12 }}>
              <List
                dataSource={detailReceipts}
                locale={{ emptyText: '暂无收款记录' }}
                renderItem={(item: any) => {
                  const receipt = item._receipt || {}
                  const amount = getReceiptAmount(item)
                  return (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Text>{receipt.company || receipt.customerName || detailVehicle.company || '-'}</Text>
                        <Text type="secondary">区间：{renderReceiptRange(item)}</Text>
                        <Text type="secondary">金额：￥{amount.toFixed(2)}</Text>
                        <Text type="secondary">
                          时间：{receipt.verifiedAt
                            ? dayjs(receipt.verifiedAt).format('YYYY-MM-DD')
                            : receipt.createdAt
                              ? dayjs(receipt.createdAt).format('YYYY-MM-DD')
                              : '-'}
                        </Text>
                      </Space>
                    </List.Item>
                  )
                }}
              />
            </Card>

            <Card size="small" title="补录租金" style={{ marginBottom: 12 }}>
              <List
                dataSource={detailRentAdditions}
                locale={{ emptyText: '暂无记录' }}
                renderItem={(add: RentAddition) => (
                  <List.Item
                    actions={[
                      <Button key="edit" type="link" onClick={() => openEditAdjustment(detailVehicle, add, 'rent_addition')}>
                        编辑
                      </Button>,
                      <Button key="delete" type="link" danger onClick={() => handleDeleteAdjustment(add, 'rent_addition')}>
                        删除
                      </Button>
                    ]}
                  >
                    <Space direction="vertical" size={0}>
                      <Text>补录租金：￥{Number(add.amount || 0).toFixed(2)}</Text>
                      <Text type="secondary">
                        日期：{add.effectiveDate || add.createdAt ? dayjs(add.effectiveDate || add.createdAt).format('YYYY-MM-DD') : '-'}
                      </Text>
                      <Text type="secondary">备注：{add.note || '-'}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>

            <Card size="small" title="车辆原值 / 配件 / 工时费用" style={{ marginBottom: 12 }}>
              <List
                dataSource={detailAdjustments}
                locale={{ emptyText: '暂无记录' }}
                renderItem={(adj: VehicleAdjustment) => (
                  <List.Item
                    actions={[
                      <Button
                        key="edit"
                        type="link"
                        onClick={() => openEditAdjustment(detailVehicle, adj, adj.type as PartsAdjustmentType)}
                      >
                        编辑
                      </Button>,
                      <Button
                        key="delete"
                        type="link"
                        danger
                        onClick={() => handleDeleteAdjustment(adj, adj.type as PartsAdjustmentType)}
                      >
                        删除
                      </Button>
                    ]}
                  >
                    <Space direction="vertical" size={0}>
                      <Text>{getAdjustmentLabel(adj.type as PartsAdjustmentType)}：￥{Number(adj.amount || 0).toFixed(2)}</Text>
                      <Text type="secondary">
                        日期：{adj.effectiveDate || adj.createdAt ? dayjs(adj.effectiveDate || adj.createdAt).format('YYYY-MM-DD') : '-'}
                      </Text>
                      <Text type="secondary">备注：{adj.note || '-'}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )}
      </Modal>

      <Modal
        title={getAdjustmentLabel(editingType)}
        open={adjustVisible}
        onCancel={() => setAdjustVisible(false)}
        onOk={handleSaveAdjustment}
        okText="保存"
      >
        <Form form={form} layout="vertical">
          {!adjustVehicle && (
            <Form.Item name="vehicleId" label="选择车辆" rules={[{ required: true, message: '请选择车辆' }]}>
              <Select
                placeholder="选择车辆"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {vehicles.map(vehicle => (
                  <Option key={vehicle._id || vehicle.id} value={vehicle._id || vehicle.id}>
                    {vehicle.plate} - {vehicle.company}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
            extra={editingType === 'rent_addition' ? '补录租金请录入正数。' : '成本项支持负数，可用于退货回库、冲减成本。'}
          >
            <InputNumber style={{ width: '100%' }} precision={2} />
          </Form.Item>

          <Form.Item name="effectiveDate" label="发生日期">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="note" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default PartsPage
